import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Loader2,
  User as UserIcon,
  Phone,
  Building2,
  CreditCard,
  IdCard,
  ArrowRight,
  LogOut,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { send } from "../components";
import { Logo } from "../components/Logo";
import { LanguageToggle } from "../components/LanguageToggle";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { banks } from "../lib/mockData";
import { Button } from "../components/ui/button";
import { generateMnIban } from "../components/service/functions";
import { useLoading } from "../hooks/useLoading";
import MerchantTermsModal from "../components/MerchantTermsModal";

const VerifyAccount = () => {
  const [firstName, setFirstName] = useState("");
  const [userType, setUserType] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [bankAccount, setBankAccount] = useState("");
  const [ibanbankAccount, setIbanBankAccount] = useState("");
  const [bankName, setBankName] = useState("");
  const [idNumber, setIdNumber] = useState("");
  const [address, setAddress] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyRegNo, setCompanyRegNo] = useState("");
  const [loading, setLoading] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [errors, setErrors] = useState({});
  const { user, setUser, setAuth } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showLoading } = useLoading()
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");

  useEffect(() => {


    fetchDataWithCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDataWithCode = async () => {
    setLoading(true);
    const res = await send("io0019", { code }, showLoading);
    setLoading(false);

    if (res.status === 'error') {
      toast.error(res.message);
      return;
    }

    // Pre-fill the form with received data
    if (res.fname) setFirstName(res.fname);
    if (res.lname) setLastName(res.lname);
    if (res.user_type) setUserType(res.user_type);
    if (res.regno) setIdNumber(res.regno);
    if (res.phone) setPhoneNumber(res.phone);
    if (res.address) setAddress(res.address);
    if (res.company_name) setCompanyName(res.company_name);
    if (res.company_regno) setCompanyRegNo(res.company_regno);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!bankName) {
      newErrors.bankName = t("selectBankError");
    }

    if (phoneNumber.length !== 8) {
      newErrors.phoneNumber = t("phoneNumberLengthError");
    }

    if (bankAccount.length < 8 || bankAccount.length > 12) {
      newErrors.bankAccount = t("bankAccountLengthError");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setShowTermsModal(true);
  };

  const handleFinalSubmit = async (contractText) => {
    setLoading(true);

    const res = await send("io0002", {
      id1: idNumber, // хэрвээ хоосон бол undefined
      fname: firstName,
      lname: lastName,
      phone: phoneNumber,
      currency: 'MNT',
      bank_code: bankName,
      acnt_code: bankAccount,
      iban: ibanbankAccount,
      contract: contractText,
    });
    setLoading(false);
    setShowTermsModal(false);

    if (res.status === 'error') {
      toast.error(res.message);
      return;
    }
    setUser(res);
    toast.success(t("accountVerifiedSuccess"));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const logout = async () => {
    setAuth({});
    await send("io0013", {}, showLoading)
    sessionStorage.clear()
    localStorage.clear()
    navigate('/');
  }


  useEffect(() => {
    if (user?.isVerified) {
      navigate("/merchant/setup");
    }
  }, [user?.isVerified, navigate]);

  useEffect(() => {
    if (bankAccount && bankName) {
      try {
        const iban = generateMnIban(
          bankName,
          bankAccount,
        );
        setIbanBankAccount(iban);
      } catch (e) {
        toast.error(e);
        setIbanBankAccount("");
      }
    }
  }, [bankAccount, bankName])

  if (user?.isVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 flex items-center justify-between p-6 bg-white z-50">
        <Logo disabled={true} />
        <div className="flex items-center gap-3">
          <LanguageToggle />
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-muted-foreground hover:text-foreground"
          >
            <LogOut className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">{t('logout')}</span>
          </Button>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-lg space-y-8 animate-fade-up">
          <div className="text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              {t("verifyAccount")}
            </h2>
            <p className="mt-2 text-muted-foreground">{t("verifySubtitle")}</p>
          </div>

          <div className="card-elevated p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              {(userType === "2" || userType === 2) && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="companyName">{t("company_name")}</Label>
                    <div className="relative">
                      <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="companyName"
                        value={companyName}
                        className="pl-11"
                        disabled
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="companyRegNo">{t("company_regno")}</Label>
                    <div className="relative">
                      <IdCard className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="companyRegNo"
                        value={companyRegNo}
                        className="pl-11"
                        disabled
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="firstName">{t("firstName")}</Label>
                <div className="relative">
                  <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="firstName"
                    placeholder="John"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="pl-11"
                    required
                    disabled
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">{t("lastName")}</Label>
                <div className="relative">
                  <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="lastName"
                    placeholder="Doe"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="pl-11"
                    required
                    disabled
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="idNumber">{t("idNumber")}</Label>
                <div className="relative">
                  <IdCard className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="idNumber"
                    placeholder="ЙЮ00000000"
                    value={idNumber}
                    onChange={(e) => setIdNumber(e.target.value.toUpperCase())}
                    className="pl-11"
                    required
                    disabled
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phoneNumber">{t("phoneNumber")}</Label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="phoneNumber"
                    type="tel"
                    placeholder="9999 9999"
                    value={phoneNumber}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      if (val.length <= 8) setPhoneNumber(val);
                    }}
                    className={`pl-11 ${errors.phoneNumber ? "border-red-500" : ""}`}
                    required
                  />

                </div>
                {errors.phoneNumber && (
                  <p className="text-sm text-red-500 mt-1 ml-1">{errors.phoneNumber}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="bankName">{t("bankName")} <span className="text-red-500">*</span></Label>
                <Select name="bankName" value={bankName} onValueChange={setBankName} required>
                  <SelectTrigger className={`h-11 ${errors.bankName ? "border-red-500" : ""}`}>
                    <div className="flex items-center gap-3">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <SelectValue placeholder={t("selectBank")} />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {banks
                      .slice() // original array-г эвдэхгүй
                      .sort((a, b) => (t(a.name)).localeCompare(t(b.name)))
                      .map((bank) => (
                        <SelectItem key={bank.id} value={bank.id}>
                          {t(bank.name)}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {errors.bankName && (
                  <p className="text-sm text-red-500 mt-1 ml-1">{errors.bankName}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="bankAccount">{t("bankAccount")}</Label>
                <div className="relative">
                  <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="bankAccount"
                    placeholder="1234567890"
                    value={bankAccount}
                    onChange={(e) => setBankAccount(e.target.value)}
                    className={`pl-11 ${errors.bankAccount ? "border-red-500" : ""}`}
                    required
                  />
                </div>
                {errors.bankAccount && (
                  <p className="text-sm text-red-500 mt-1 ml-1">{errors.bankAccount}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="ibanbankAccount">{t("ibanbankAccount")}</Label>
                <div className="relative">
                  <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="ibanbankAccount"
                    placeholder="1234567890"
                    value={ibanbankAccount}
                    onChange={(e) => setIbanBankAccount(e.target.value)}
                    className="pl-11"
                    required
                    disabled
                  />
                </div>
              </div>

              <Button
                type="submit"
                variant="gradient"
                size="lg"
                className="w-full mt-6"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("loading")}
                  </>
                ) : (
                  <>
                    {t("continue")}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>

        <MerchantTermsModal
          open={showTermsModal}
          onOpenChange={setShowTermsModal}
          onConfirm={handleFinalSubmit}
          loading={loading}
          address={address}
          idNumber={idNumber}
          lastName={lastName}
          firstName={firstName}
          userType={userType}
          companyName={companyName}
          companyRegNo={companyRegNo}
        />
      </main>
    </div>
  );
};

export default VerifyAccount;
